import {Button, RelativeTime} from '@primer/react'
import {CommentIcon} from '@primer/octicons-react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {
  TimelineEvent,
  ReviewTimelineEvent,
  CommitTimelineEvent,
  LabeledTimelineEvent,
  ReviewRequestedTimelineEvent,
} from '../../routes/route-payload-types'
import {getEventVerb} from './catch-up-helpers'
import styles from './CatchUp.module.css'
import {Link} from '@github-ui/react-core/link'

interface EventListProps {
  events: TimelineEvent[]
  type: string
  changesUrl: string
  conversationUrl: string
}

export function EventList({events, type, changesUrl, conversationUrl}: EventListProps) {
  const verb = getEventVerb(type)

  return (
    <ul className={styles.eventList}>
      {events.map(event => {
        const eventId = `${event.user.id}-${event.timestamp}`

        // Determine the actual verb for this specific event
        // For labeled/unlabeled, check the specific event type
        let eventVerb = verb
        let eventText = ''

        if (type === 'labeled') {
          const labeledEvent = event as LabeledTimelineEvent
          const isUnlabeled = labeledEvent.additionalInfo.eventType === 'unlabeled'
          const labelName = labeledEvent.additionalInfo.labelName

          if (labelName) {
            eventText = isUnlabeled ? `Removed label "${labelName}" by ` : `Added label "${labelName}" by `
          } else {
            eventVerb = isUnlabeled ? 'unlabeled by' : 'labeled by'
          }
        } else if (type === 'review-requested') {
          const reviewRequestedEvent = event as ReviewRequestedTimelineEvent
          const isRemoval = reviewRequestedEvent.additionalInfo.eventType === 'review_request_removed'
          const requestedReviewer = reviewRequestedEvent.additionalInfo.requestedReviewer

          if (requestedReviewer) {
            eventText = isRemoval
              ? `Removed review request for ${requestedReviewer} by `
              : `Requested review from ${requestedReviewer} by `
          } else {
            eventVerb = isRemoval ? 'removed review request by' : 'requested review by'
          }
        }

        // For commits, add a button link to individual commit
        if (type === 'commit') {
          const commitEvent = event as CommitTimelineEvent
          const commitUrl = `${changesUrl}/${commitEvent.additionalInfo.sha}`
          const shortSha = commitEvent.additionalInfo.sha.substring(0, 7)
          return (
            <li key={eventId} className={styles.event}>
              <span className={styles.eventText}>
                Commit&nbsp;<code className={styles.eventSha}>{shortSha}</code>&nbsp;{eventVerb}&nbsp;
                <GitHubAvatar className="mr-1" src={event.user.avatarUrl} size={20} />
                <strong>{event.user.login}</strong>
              </span>
              <span className={styles.eventTimestamp}>
                <RelativeTime date={new Date(event.timestamp)} />
              </span>
              <Button as={Link} to={commitUrl} size="small" variant="invisible" aria-label={`View commit ${shortSha}`}>
                View commit
              </Button>
            </li>
          )
        }

        // For reviews (approval, changes-requested, review), add a button link to review threads
        if (type === 'approval' || type === 'changes-requested' || type === 'review') {
          const reviewEvent = event as ReviewTimelineEvent
          const reviewId = reviewEvent.additionalInfo.reviewId
          const commentCount = reviewEvent.additionalInfo.commentCount
          const reviewUrl = `${conversationUrl}#pullrequestreview-${reviewId}`
          return (
            <li key={eventId} className={styles.event}>
              <span className={styles.eventText}>
                <span>{eventText || `${eventVerb.charAt(0).toUpperCase() + eventVerb.slice(1)}`}</span>
                &nbsp;
                <GitHubAvatar className="mr-1" src={event.user.avatarUrl} size={20} />
                <strong>{event.user.login}</strong>
              </span>
              <span className={styles.eventTimestamp}>
                <RelativeTime date={new Date(event.timestamp)} />
              </span>
              <Button
                as={Link}
                to={reviewUrl}
                size="small"
                variant="invisible"
                aria-label="View review threads"
                leadingVisual={CommentIcon}
                count={commentCount ? commentCount : undefined}
              >
                View
              </Button>
            </li>
          )
        }

        return (
          <li key={eventId} className={styles.event}>
            <span className={styles.eventText}>
              <span>{eventText || `${eventVerb.charAt(0).toUpperCase() + eventVerb.slice(1)}`}&nbsp;</span>
              <GitHubAvatar className="mr-1" src={event.user.avatarUrl} size={20} />
              <strong>{event.user.login}</strong>
            </span>
            <span className={styles.eventTimestamp}>
              <RelativeTime date={new Date(event.timestamp)} />
            </span>
          </li>
        )
      })}
    </ul>
  )
}
